import { calculateTotal } from '@calculations/price/utilities/total';
import { SnapRoundingOptionsCustom } from '@calculations/round';
import { PriceStrategyConfigWithRelations } from '@snap-types/models/price-strategy-config';
import { SnapSuggestionStatus } from '@snap-types/models/sort-status';
import { Decimal } from '@snap-types/types/decimal';
import { WritableQuoteBuilderStateType } from '@ui/features/quote-builder/types/state';

import {
  getQuoteLineOrThrow,
  getQuotePriceOrThrow,
} from '../../utilities/action-guards';
import { OnContributionMarginChangeInput } from '../types';
import { applyQuotePriceRecalcResult } from '../utilities/apply-quote-price-recalc-result';
import { buildStrategyCalculations } from '../utilities/build-strategy-calculations';

export function onPartContributionMarginChange(
  state: WritableQuoteBuilderStateType,
  params: {
    quoteLineUuid: string;
    quotePriceUuid: string;
    contributionMargin: Decimal;
    priceStrategyConfigs: PriceStrategyConfigWithRelations[];
  },
) {
  const quoteLine = getQuoteLineOrThrow(state, params.quoteLineUuid);
  const quotePrice = getQuotePriceOrThrow(
    state,
    params.quoteLineUuid,
    params.quotePriceUuid,
  );
  const { priceStrategy } = quotePrice;

  const inputs = {
    state,
    quoteLine,
    quotePrice,
    contributionMargin: params.contributionMargin,
    priceStrategyConfigs: params.priceStrategyConfigs,
  } satisfies OnContributionMarginChangeInput;

  const calculation = buildStrategyCalculations('onContributionMarginChange')[
    priceStrategy
  ];

  if (!calculation) {
    console.error(
      'No contribution margin calculation function found for price strategy',
      priceStrategy,
    );
    return;
  }

  const output = calculation(inputs);

  if (output.status === 'failure') {
    // @TODO(NT): Our store should be integrated with toast notifications
    console.error('Failed to calculate price', output.reason);
    return;
  }

  applyQuotePriceRecalcResult(state, {
    quoteLineUuid: params.quoteLineUuid,
    quotePriceUuid: params.quotePriceUuid,
    quotePrice,
    patch: {
      contributionMargin: params.contributionMargin,
      calculatedContributionMargin: null,
      userContributionMargin: params.contributionMargin,
    },
  });

  if (output.status === 'success') {
    const { grossMargin, unitPrice } = output;

    applyQuotePriceRecalcResult(state, {
      quoteLineUuid: params.quoteLineUuid,
      quotePriceUuid: params.quotePriceUuid,
      quotePrice,
      patch: {
        unitPrice,
        calculatedUnitPrice: unitPrice,
        userUnitPrice: null,

        grossMargin,
        calculatedGrossMargin: grossMargin,

        totalPrice: calculateTotal(
          unitPrice,
          quotePrice.quantity,
          SnapRoundingOptionsCustom(state),
        ),
        suggestedPriceStatus: SnapSuggestionStatus.REJECTED,
      },
      rawFieldsToClear: ['unitPrice'],
    });
  }

  quoteLine.automatable = false;

  if (quotePrice.suggestedPriceStatus === SnapSuggestionStatus.REJECTED) {
    quoteLine.suggestedPricesStatus = SnapSuggestionStatus.REJECTED;
  }
}
